import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './rootReducer';
import axios from 'axios';
import { updateNotification } from './serviceSlice';
import { Localization, FetchTypes, SliceTypes } from 'utils';
import { BASE_REACT_APP_CF_SCENARIO_PLAYING_AND_DRIVERS_OF_GROWTH } from '../App';
import { createScenarioInitial } from './InitialStates/createScenarioInitial';

export interface PlotlySelectionProps {
    highlighted: string;
    per_anum: string;
}

export interface PayloadScenarioUKProps {
    sex: string;
    age_grp: string[];
    heritage: string[];
    category: string[];
    percent: number;
}

export interface PayloadScenarioUSProps {
    GEO_UNIT: string;
    GEO_NAME: string;
    plot_type: string;
    SEX: string;
    AGE_GRP: string;
    ETH_GRP: string;
    CATEGORY: string;
    PCT: number;
}

export interface FetchScenarioPlotsProps {
    localization: Localization;
    payload: PayloadScenarioUKProps | any;
    selection: any;
}

export const fetchScenarioPlots: any = createAsyncThunk(
    FetchTypes.SCENARIO_PLOTS,
    async (params: FetchScenarioPlotsProps, thunkApi: any) => {
        const { localization, payload, selection } = params;
        try {
            const bodyData = {
                ...payload,
            };
            const response = await axios.post(
                BASE_REACT_APP_CF_SCENARIO_PLAYING_AND_DRIVERS_OF_GROWTH,
                bodyData,
            );
            thunkApi.dispatch(
                updateNotification({
                    type: 'success',
                    header: 'Create scenario',
                    message: 'Data for your updated selection has been successfully loaded.',
                    duration: 5000,
                }),
            );
            return {
                localization,
                payload: response.data.scenario_plots,
                selection,
            };
        } catch (err: any) {
            thunkApi.dispatch(
                updateNotification({
                    type: 'error',
                    header: 'Create Scenario',
                    message: `Something went wrong!`,
                    duration: 5000,
                }),
            );
            throw err;
        }
    },
);

export const createScenarioSlice = createSlice({
    name: SliceTypes.CREATE_SCENARIO,
    initialState: createScenarioInitial,
    reducers: {
        updatePlotlySelection: (
            state,
            {
                payload: { localization, data },
            }: PayloadAction<{
                localization: Localization;
                data: { value: string; key: string };
            }>,
        ) => {
            const { value, key } = data;
            state[localization].plotlySelection = {
                ...state[localization].plotlySelection,
                [key]: value,
            };
        },
        updateScenarioSelectionState: (
            state,
            {
                payload: { localization, data },
            }: PayloadAction<{
                localization: Localization;
                data: { value: any; key: string };
            }>,
        ) => {
            const { value, key } = data;
            state[localization].selectionState = {
                ...state[localization].selectionState,
                [key]: value,
            };
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchScenarioPlots.pending, (state, action) => {
            const { localization, selection }: FetchScenarioPlotsProps = action.meta.arg;
            state[localization].plotlySelection = state[localization].plotlyInitial;
            state[localization].selectionState = selection;
            state[localization].isDataLoading = true;
        });
        builder.addCase(fetchScenarioPlots.fulfilled, (state, action) => {
            const { payload, localization }: FetchScenarioPlotsProps = action.payload;
            state[localization].isDataLoading = false;
            state[localization].data = { ...payload };
            state[localization].dataLoaded = true;
        });
        builder.addCase(fetchScenarioPlots.rejected, (state, action) => {
            const { localization }: FetchScenarioPlotsProps = action.meta.arg;
            state[localization].selectionState.category = [];
            state[localization].isDataLoading = false;
        });
    },
});

export const { updatePlotlySelection, updateScenarioSelectionState } = createScenarioSlice.actions;

export const scenarioSlice = (state: RootState) => state.createScenario;

export default createScenarioSlice.reducer;
